import { Component, Inject, OnInit, signal } from '@angular/core';
import { MatIconModule } from "@angular/material/icon";
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef, MatDialog } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { SelectSearchComponent } from '../../../shared/components/select-search/select-search.component';
import { DepackService } from '../../../../services/depack.service';
import { LookUpService } from '../../../../services/lookupservice';
import { forkJoin } from 'rxjs';
import { numericInputLimit } from '../../../shared/commonutils/helper';
import { AlertDialogComponent } from '../../../shared/components/alert-dialog/alert-dialog.component';
import { SaveAttemptErrorMatcher } from '../../../shared/commonutils/errorStateDirective';

@Component({
  selector: 'app-save-outbound-stream',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatIconModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    SelectSearchComponent
  ],
  templateUrl: './save-outbound-stream.component.html',
  styleUrl: './save-outbound-stream.component.scss'
})
export class SaveOutboundStreamComponent implements OnInit {
  streamForm!: FormGroup;
  isDriverMode = false;
  streamsData: any[] = []
  streams: any[] = [];
  uoms: any[] = [];
  attemptSave = signal(false);
  public matcher = new SaveAttemptErrorMatcher(() => this.attemptSave());
  displayValue = signal<string>('');
  task: string = 'Add';
  statusId = 0;
  selectedStreamIsRevenue = false;
  completeStatusId = 8;
  customerId: string = '';
  constructor(
    private fb: FormBuilder, private depackService: DepackService, private lookUp: LookUpService,
    private dialogRef: MatDialogRef<SaveOutboundStreamComponent>, @Inject(MAT_DIALOG_DATA) public data: any,
    public dialog: MatDialog){

  }

  ngOnInit(): void {
    console.log("outbound stream input", this.data);
    this.task = this.data.task;
    this.customerId = this.data.customerId;
    this.statusId = Number(this.data.statusId ?? 0);
    this.isDriverMode = this.data.driverMode === true;
    this.streamForm = this.fb.group({
      streamId: [null, Validators.required],
      quantity: [null, Validators.required],
      qtyUomId: [null, Validators.required],

      siteUomId: [null],
      siteWeight: [''],

      custUomId: [{ value: null, disabled: this.isDriverMode }],
      custWeight: [{ value: '', disabled: this.isDriverMode }],

      tons: [{ value: '', disabled: this.isDriverMode }],

      unitPrice: ['']
    });

    this.loadDropdowns();
  }

  loadDropdowns(){
    forkJoin({
      streams: this.depackService.getDeepackOutboundStreams(parseInt(this.customerId)),
      uoms: this.lookUp.getMaterialUnitOfMeasureLookup(0)
    }).subscribe({
      next: ({ streams, uoms }) => {
        this.streamsData = streams;
        this.streams = streams.map((x: any) => ({
          id: x.id,
          name: x.name
        }));

        this.uoms = uoms.map((x: any) => ({
          id: x.id,
          name: x.name
        }));

        this.patchEditData();        

        const streamId = this.streamForm.get('streamId')?.value;
        this.selectedStreamIsRevenue =
          this.streamsData.find(x => x.id === streamId)?.isRevenue === true;
        
        this.updateCompleteValidators();
      }
    });    
  }

  patchEditData(): void {
    if (this.data.task !== 'Edit' || !this.data.editData) {
      return;
    }
  
    const row = this.data.editData;
    console.log("editing row", row);
    this.streamForm.patchValue({
      streamId: row.streamID ?? row.streamId,
      quantity: row.quantity,
      qtyUomId: row.uom ?? row.uOM ?? row.qtyUomId,
  
      siteUomId: row.cBD_UOM ?? row.siteUomId,
      siteWeight: row.cBD_WT ?? row.siteWeight,
  
      custUomId: row.cUST_UOM ?? row.custUomId,
      custWeight: row.cUST_WT ?? row.custWeight,
  
      tons: row.tons,
      unitPrice: row.unitPrice
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    this.attemptSave.set(true);
    this.streamForm.markAllAsTouched();
    if (this.streamForm.invalid) {
      return;
    }

    const formValue = this.streamForm.getRawValue();
    const editRow = this.data.editData;
    const alreadyExists = this.data.existingStreams?.find((x: any) => {
      const sameStream = (x.streamID ?? x.streamId) === formValue.streamId;    
      const sameRow =
        editRow &&
        (
          (x.id && editRow.id && x.id === editRow.id) ||
          (x.tempKey && editRow.tempKey && x.tempKey === editRow.tempKey)
        );
    
      return sameStream && !sameRow;
    });
  
    if (alreadyExists) {
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Alert',
          message: `'${alreadyExists.streamName}' is already added.`
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }

    const selectedStream = this.streams.find(x => x.id === formValue.streamId);
    const streamDetails = this.streamsData.find(x => x.id === formValue.streamId);
  
    const qtyUom = this.uoms.find(x => x.id === formValue.qtyUomId);
  
    const siteUom = this.uoms.find(x => x.id === formValue.siteUomId);
  
    const custUom = this.uoms.find(x => x.id === formValue.custUomId);
  
    const payload = {
      id: this.data.editData?.id ?? 0,
      fSLoadID: this.data.editData?.fSLoadID ?? 0,
      tempKey: this.data.editData?.tempKey,
      task: this.data.task,
      ...formValue,
      streamName: selectedStream?.name ?? '',  
      isRevenue: streamDetails?.isRevenue ?? false,
      uOMText: qtyUom?.name ?? '',
      uOM: qtyUom?.id,
      quantity: formValue.quantity === '' || formValue.quantity == null
                ? null
                : Number(formValue.quantity),
      cBD_WT: formValue.siteWeight === '' || formValue.siteWeight == null
                ? null
                : Number(formValue.siteWeight),
      cBD_UOM: siteUom?.id,
      cbdUOMText: siteUom?.name ?? '',
      
      cUST_UOM: custUom?.id,
      cUST_WT: formValue.custWeight === '' || formValue.custWeight == null
              ? null
              : Number(formValue.custWeight),
      custUOMText: custUom?.name ?? '',
      tons: formValue.tons === '' || formValue.tons == null
                  ? null
                  : Number(formValue.tons),    
      unitPrice: formValue.unitPrice === '' || formValue.unitPrice == null
                  ? null
                  : Number(formValue.unitPrice),
      total: Number((((formValue.custWeight === '' || formValue.custWeight == null) ? 0 : Number(formValue.custWeight))
              *
              ((formValue.unitPrice === '' || formValue.unitPrice == null) ? 0 : Number(formValue.unitPrice))).toFixed(2))
    };
  
    this.dialogRef.close(payload);
  }

  roundOff(event: Event, maxValue: number, maxDecimals: number, controlName: string): void {
    const input = event.target as HTMLInputElement;
    let val = input.value;

    val = numericInputLimit(event, maxValue, maxDecimals);

    this.displayValue.set(val);
    input.value = val;

    // 6. Parsing for the backing variables
    const isValueEmpty = val === '' || val === '.';
    if (isValueEmpty) {
      this.streamForm.get(controlName)?.setValue(null, { emitEvent: false });
    } else if (val.endsWith('.')) {
      this.streamForm.get(controlName)?.setErrors({ 'incompleteDecimal': true });
    } else {
      const parsedValue = parseFloat(val);
      this.streamForm.get(controlName)?.setValue(parsedValue, { emitEvent: false });
    }
    requestAnimationFrame(() => {
      if (document.activeElement === input) {
          input.value = val;
      }
    });
  }

  onStreamreceived(streamId: number| null){
    const streamDetails = this.streamsData.find(x => x.id === streamId);
    this.selectedStreamIsRevenue = streamDetails?.isRevenue === true;
  
    this.updateCompleteValidators();
  }

  updateCompleteValidators(): void {
    const isComplete = this.statusId === this.completeStatusId;
  
    const custUom = this.streamForm.get('custUomId');
    const custWeight = this.streamForm.get('custWeight');
    const actualTons = this.streamForm.get('tons');
    const unitPrice = this.streamForm.get('unitPrice');
  
    if (!this.isDriverMode && isComplete) {
      custUom?.enable();
      custWeight?.enable();
      actualTons?.enable();
  
      custUom?.setValidators([Validators.required]);
      custWeight?.setValidators([Validators.required]);
      //actualTons?.setValidators([Validators.required]);
  
      if (this.selectedStreamIsRevenue) {
        unitPrice?.enable();
        unitPrice?.setValidators([Validators.required]);
      } else {
        unitPrice?.clearValidators();
        unitPrice?.setValue(null);
        unitPrice?.disable();
      }
    } else {
      custUom?.clearValidators();
      custWeight?.clearValidators();
      actualTons?.clearValidators();
      unitPrice?.clearValidators();
  
      if (this.isDriverMode) {
        custUom?.disable();
        custWeight?.disable();
        actualTons?.disable();
        unitPrice?.disable();
      } else {
        custUom?.enable();
        custWeight?.enable();
        actualTons?.enable();
  
        if (this.selectedStreamIsRevenue) {
          unitPrice?.enable();
          unitPrice?.setValidators([Validators.required]);
        } else {
          unitPrice?.setValue(null);
          unitPrice?.disable();
        }
      }
    }
  
    custUom?.updateValueAndValidity();
    custWeight?.updateValueAndValidity();
    //actualTons?.updateValueAndValidity();
    unitPrice?.updateValueAndValidity();
  }
}
